/* eslint-disable react-refresh/only-export-components */
import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/lib/component-variants'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  loadingText?: string
  'aria-describedby'?: string
  'aria-expanded'?: boolean
  'aria-haspopup'?: boolean | 'false' | 'true' | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, loadingText, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    
    const isDisabled = disabled || loading;
    
    return (
      <Comp 
        className={cn(buttonVariants({ variant, size, className }))} 
        ref={ref} 
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        {...props} 
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <LoadingSpinner 
              size="sm" 
              color={variant === 'outline' || variant === 'ghost' || variant === 'link' ? 'primary' : 'white'}
              aria-hidden="true"
            />
            <span>{loadingText || 'Loading...'}</span>
          </div>
        ) : (
          children
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
